const RESPONSIVE_COLUMN_WIDTH_PX = {
  xsmall: 80,
  small: 100,
  medium: 180,
  large: 240,
  audio: 200,
  metric: 85,
} as const

const RESPONSIVE_COLUMN_WIDTHS: Record<string, number> = {
  simulation_output_id: RESPONSIVE_COLUMN_WIDTH_PX.xsmall,
  created_at: RESPONSIVE_COLUMN_WIDTH_PX.small,
  occurred_at: RESPONSIVE_COLUMN_WIDTH_PX.small,
  audio: RESPONSIVE_COLUMN_WIDTH_PX.audio,
  customer_metadata: RESPONSIVE_COLUMN_WIDTH_PX.large,
  reviewer_assignees: RESPONSIVE_COLUMN_WIDTH_PX.medium,
  metric_default: RESPONSIVE_COLUMN_WIDTH_PX.metric,
  default: RESPONSIVE_COLUMN_WIDTH_PX.small,
}

export const UPLOADED_CONVERSATION_TABLE_COLUMN_WIDTHS = {
  audio: "200px",
  timestamp: "130px",
  standard: "220px",
  metadata: "600px",
  description: "320px",
} as const

const METRIC_COLUMN_PREFIX = "metric_"

function getResponsiveColumnWidth(columnKey: string): number | undefined {
  return RESPONSIVE_COLUMN_WIDTHS[columnKey]
}

export interface VirtualConversationsTableColumn {
  key: string
  label: string
  fullLabel?: string
  width?: string
}

function parsePixelWidth(width: string | undefined): number | null {
  if (!width) {
    return null
  }

  const match = width.trim().match(/^(\d+(?:\.\d+)?)px$/)
  if (!match) {
    return null
  }

  return Number(match[1])
}

function getConversationsColumnWidth(
  column: Pick<VirtualConversationsTableColumn, "key">,
): number {
  const columnWidth = getResponsiveColumnWidth(column.key)
  if (columnWidth) {
    return columnWidth
  }
  if (column.key.startsWith(METRIC_COLUMN_PREFIX)) {
    return RESPONSIVE_COLUMN_WIDTHS.metric_default
  }
  return RESPONSIVE_COLUMN_WIDTHS.default
}

/**
 * Preferred fixed width (px) for an uploaded conversation column: the larger of the
 * per-key default and any explicit `width`. Fed to the shared `@coval/ui`
 * Table as each `ColumnDef.width`.
 */
export function getPreferredConversationsColumnWidth(
  column: VirtualConversationsTableColumn,
): number {
  return Math.max(
    getConversationsColumnWidth(column),
    parsePixelWidth(column.width) ?? 0,
  )
}

export function getResponsiveConversationsColumns({
  columns,
  visibleColumns,
}: {
  columns: VirtualConversationsTableColumn[]
  visibleColumns: Set<string>
}): VirtualConversationsTableColumn[] {
  // Narrow panes should expose horizontal scrolling, not silently drop columns.
  return columns.filter((col) => visibleColumns.has(col.key))
}
